import { motion, type Variants } from "framer-motion";
import { useEffect, useRef } from "react";
import { studioClients } from "../../assets/studio/clients";
import { StudioServices } from "../../assets/studio/studioServicesData";
import IconCard from "../../components/IconCard";
import ProjectShowcase from "../../components/ProjectsShowcase";
import SectionHeading from "../../components/SectionHeading";
import StudioCard from "../../components/StudioCard";
import StudioForm from "../../components/StudioForm";
import StudioHero from "../../components/StudioHero";
import Subheading from "../../components/Subheading";
import { useLocation, useNavigate } from "react-router";
import useIsVisible from "../../hooks/useIsVisible";
import { isMobile } from "react-device-detect";

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const moveUp: Variants = {
  hidden: { opacity: 0, y: 60 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeInOut",
      delay: i * 0.15,
    },
  }),
};

const Studio = () => {
  const contactRef = useRef<HTMLDivElement | null>(null);
  const { hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (hash.includes("#contact")) {
      const contactDiv = document.getElementById("contact");
      contactDiv?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, [hash]);

  const removeHashFromUrl = () => {
    navigate("/studio", { replace: true });
  };

  useIsVisible(contactRef, removeHashFromUrl);

  return (
    <div className="h-full">
      {/* Hero section */}
      <StudioHero />

      <ProjectShowcase />

      {/* Services Section */}
      <section className="px-6 lg:px-15 xl:px-30 py-12 xl:py-30 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-4 md:gap-6 xl:gap-8 xl:gap-y-4">
        <SectionHeading text="Services We Offer" className="col-span-full pb-4" />

        <motion.div
          className="grid grid-cols-subgrid col-span-full w-full gap-y-8 lg:gap-y-0"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: isMobile ? 0.15 : 0.65 }}
        >
          {StudioServices.map((service, i) => (
            <motion.div
              key={service.name}
              custom={i}
              variants={moveUp}
              className="col-span-full md:col-span-3 lg:col-span-2 xl:col-span-3"
            >
              <IconCard
                src={service.icon}
                route="/"
                hoverSrc="/"
                alt="/"
                title={service.name}
                description={service.description}
              />
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Clients Section */}
      <section className="px-6 lg:px-15 xl:px-30 py-12 xl:py-30 bg-black grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-4 md:gap-6 xl:gap-8 xl:gap-y-8">
        <SectionHeading
          text="Discover more about our work"
          className="col-span-full lg:col-span-9"
        />

        <div className="flex flex-col gap-4 col-span-full xl:col-span-9">
          <Subheading text="Live Projects" className="text-white" />

          <p className="text-sm md:text-base 3xl:text-lg flex items-center text-white">
            Our work speaks through the experiences we craft and the impact we create. From
            redefining luxury travel with Aterna, to building seamless digital ecosystems like
            DietHub, and shaping the future of immersive education with VEROlabs, each project
            reflects our commitment to innovation and excellence. Here are some of our most
            outstanding projects in action.
          </p>
        </div>

        <motion.div
          className="grid grid-cols-subgrid col-span-full gap-y-12 lg:gap-y-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: isMobile ? 0.15 : 0.65 }}
          variants={{ visible: { transition: { staggerChildren: 0.25 } } }}
        >
          {studioClients.map((client, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="col-span-full md:col-span-4 xl:col-span-6"
            >
              <StudioCard {...client} />
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Contact Section */}
      <section className="px-6 lg:px-15 xl:px-30 py-12 xl:py-30 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12 gap-4 md:gap-6 xl:gap-8 xl:gap-y-8">
        <div className="col-span-full lg:col-start-1 xl:col-start-2 lg:col-span-4 xl:col-span-5 grid grid-cols-subgrid gap-y-4">
          <SectionHeading text="Get in Touch" className="col-span-full lg:text-center" />
          <div className="flex flex-col gap-4 col-span-full xl:col-span-5">
            <Subheading
              text="Let’s Build Something Exceptional Together"
              className="lg:text-center"
            />
            <p className="text-sm md:text-base 3xl:text-lg flex items-center lg:text-center">
              We’d love to hear about your vision and how we can bring it to life. Share your ideas,
              and our team will get back to you to explore the next steps.
            </p>
          </div>
          <img
            src="/images/studio/StudioTable.svg"
            alt=""
            className="col-span-full w-full lg:pt-7"
          />
        </div>

        {/* contact form */}
        <div
          ref={contactRef}
          id="contact"
          className="col-span-full lg:col-span-3 xl:col-span-4 lg:col-start-6 xl:col-start-8 grid grid-cols-subgrid"
        >
          <StudioForm />
        </div>
      </section>
    </div>
  );
};

export default Studio;
